import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-email-verification',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="verification-container">
      <div class="verification-card">
        <div class="verification-header">
          <span class="verification-icon">📧</span>
          <h2>Verificación de Email</h2>
        </div>

        <div class="verification-content">
          <div *ngIf="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Verificando tu email...</p>
          </div>

          <div *ngIf="!loading && success" class="success-state">
            <span class="success-icon">✅</span>
            <h3>¡Email verificado correctamente!</h3>
            <p>Tu cuenta ha sido activada. Ya puedes acceder a todas las funcionalidades de BarClik.</p>
            <button class="continue-btn" (click)="goToDashboard()">
              Continuar al Dashboard
            </button>
          </div>

          <div *ngIf="!loading && !success" class="error-state">
            <span class="error-icon">❌</span>
            <h3>Error en la verificación</h3>
            <p>{{ errorMessage }}</p>
            <div class="error-actions">
              <button class="resend-btn" (click)="resendVerification()" [disabled]="resending">
                {{ resending ? 'Enviando...' : 'Reenviar email de verificación' }}
              </button>
              <a routerLink="/" class="back-link">Volver al inicio</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .verification-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #22C55E 0%, #121212 100%);
      padding: 1rem;
    }

    .verification-card {
      background: white;
      border-radius: 20px;
      padding: 2rem;
      max-width: 500px;
      width: 100%;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
      text-align: center;
    }

    .verification-header {
      margin-bottom: 2rem;
    }

    .verification-icon {
      font-size: 4rem;
      display: block;
      margin-bottom: 1rem;
    }

    .verification-header h2 {
      color: #1e293b;
      font-size: 1.8rem;
      font-weight: 700;
      margin: 0;
    }

    .loading-state {
      padding: 2rem 0;
    }

    .spinner {
      width: 40px;
      height: 40px;
      border: 4px solid #f1f5f9;
      border-top: 4px solid #22C55E;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 1rem;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .success-state,
    .error-state {
      padding: 1rem 0;
    }

    .success-icon,
    .error-icon {
      font-size: 3rem;
      display: block;
      margin-bottom: 1rem;
    }

    .success-state h3 {
      color: #059669;
      margin-bottom: 1rem;
    }

    .error-state h3 {
      color: #dc2626;
      margin-bottom: 1rem;
    }

    .success-state p,
    .error-state p {
      color: #64748b;
      line-height: 1.6;
      margin-bottom: 2rem;
    }

    .continue-btn {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      color: white;
      border: none;
      padding: 0.75rem 2rem;
      border-radius: 25px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      font-size: 1rem;
    }

    .continue-btn:hover {
      background: linear-gradient(135deg, #059669 0%, #047857 100%);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }

    .error-actions {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      align-items: center;
    }

    .resend-btn {
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
      color: white;
      border: none;
      padding: 0.75rem 1.5rem;
      border-radius: 25px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .resend-btn:hover:not(:disabled) {
      background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
      transform: translateY(-2px);
    }

    .resend-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }

    .back-link {
      color: #22C55E;
      text-decoration: none;
      font-weight: 500;
      transition: color 0.3s ease;
    }

    .back-link:hover {
      color: #10B981;
      text-decoration: underline;
    }

    @media (max-width: 480px) {
      .verification-card {
        padding: 1.5rem;
        margin: 0.5rem;
      }

      .verification-icon {
        font-size: 3rem;
      }

      .verification-header h2 {
        font-size: 1.5rem;
      }
    }
  `]
})
export class EmailVerificationComponent implements OnInit {
  loading = true;
  success = false;
  errorMessage = '';
  resending = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    const token = this.route.snapshot.queryParams['token'];
    if (token) {
      this.verifyEmail(token);
    } else {
      this.loading = false;
      this.errorMessage = 'Token de verificación no encontrado';
    }
  }

  private verifyEmail(token: string): void {
    this.authService.verifyEmail(token).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.success) {
          this.success = true;
        } else {
          this.errorMessage = response.message || 'Error al verificar el email';
        }
      },
      error: (error) => {
        this.loading = false;
        this.errorMessage = error.message || 'Error al verificar el email';
      }
    });
  }

  async resendVerification(): Promise<void> {
    this.resending = true;
    
    try {
      const currentUser = this.authService.getCurrentUser();
      const email = currentUser?.email || '';
      const response = await this.authService.resendActivationEmail(email);
      if (response.success) {
        this.errorMessage = 'Se ha enviado un nuevo email de verificación';
      } else {
        this.errorMessage = response.message || 'Error al reenviar email';
      }
    } catch (error: any) {
      this.errorMessage = error.message || 'Error al reenviar email';
    } finally {
      this.resending = false;
    }
  }

  goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }
}